import React, { useState } from 'react';
import { Eye, EyeOff, LogIn, UserPlus, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginRequest } from '../../types';

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  const { login, loading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: '',
    rememberMe: false,
  });
  
  const [showPassword, setShowPassword] = useState(false);
  // Set by the SSO callback redirect: /login?sso_error=no_account
  const [ssoError] = useState(() =>
    new URLSearchParams(window.location.search).get('sso_error')
  );
  const [validationErrors, setValidationErrors] = useState<Partial<LoginRequest>>({});

  const validateForm = (): boolean => {
    const errors: Partial<LoginRequest> = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await login(formData);
      // Redirect will be handled by App.tsx based on auth state
    } catch (error) {
      // Error is handled by context
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: inputValue }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name as keyof LoginRequest]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-4">
      <div className="max-w-md w-full">
        <div className="bg-base-100 rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2">PingPal</h1>
            <h2 className="text-xl font-semibold mb-2">Welcome Back</h2>
            <p className="text-base-content/70">Sign in to your account</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-error mb-6">
              <span>{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Username</span>
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`input input-bordered w-full ${
                  validationErrors.username ? 'input-error' : ''
                }`}
                placeholder="Enter your username"
                disabled={loading}
              />
              {validationErrors.username && (
                <label className="label">
                  <span className="label-text-alt text-error">{validationErrors.username}</span>
                </label>
              )}
            </div>

            {/* Password Field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Password</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`input input-bordered w-full pr-12 ${
                    validationErrors.password ? 'input-error' : ''
                  }`}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/60 hover:text-base-content"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">{validationErrors.password}</span>
                </label>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="checkbox checkbox-primary"
                  disabled={loading}
                />
                <span className="label-text">Remember me for 30 days</span>
              </label>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              className={`btn btn-primary w-full ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Signing In...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>

            {/* Keycloak SSO */}
            <a
              href="/api/auth/oidc/login"
              className="btn btn-outline w-full mt-3"
              onClick={() => clearError()}
            >
              <KeyRound className="w-5 h-5" />
              Sign in with iLGaming SSO
            </a>
            {ssoError && (
              <div className="alert alert-warning mt-3 text-sm">
                <span>
                  {ssoError === 'no_account'
                    ? 'That SSO account has no PingPal user yet. Sign in with a password, then ask an admin to add your email.'
                    : ssoError === 'disabled'
                      ? 'That account is disabled in PingPal.'
                      : 'SSO sign-in failed. Try again or use your password.'}
                </span>
              </div>
            )}
          </form>

          {/* Switch to Register */}
          <div className="divider">New to PingPal?</div>
          <button
            onClick={onSwitchToRegister}
            className="btn btn-outline btn-secondary w-full"
            disabled={loading}
          >
            <UserPlus className="w-5 h-5" />
            Create Account
          </button>

          {/* Default Admin Notice */}
          {import.meta.env.DEV && (
          <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="text-sm text-warning-content">
              <strong>Dev seeded admin:</strong>
              <br />
              Username: <code className="bg-base-300 px-1 rounded">admin</code>
              <br />
              Password: <code className="bg-base-300 px-1 rounded">admin123</code>
              <br />
              <em className="text-xs">Local development only. Never rendered in a production build.</em>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}; 